import { Event } from 'types/shared/documents/restaurants';
import { ConnectedTemplateShift, EventTemplate } from 'types/eventTemplates';
import isEqual from 'lodash/isEqual';
import { cloneDeep } from './data-utils';

const TEMPLATE_METADATA_KEYS = [
  'archivedAt',
  'archivedBy',
  'canArchive',
  'connectedShifts',
  'createdAt',
  'createdBy',
  'dates',
  'dateShifts',
  'draft',
  'shifts',
  'status',
  'templateName',
  'updatedAt',
  'updatedBy',
];

export function eventFromTemplate(template: EventTemplate): Event {
  const event = cloneDeep(template) as unknown as Record<string, unknown>;
  TEMPLATE_METADATA_KEYS.forEach((key) => delete event[key]);
  delete event.edited;
  return event as unknown as Event;
}

export function duplicateTemplateEvent(template: EventTemplate): Event {
  return eventFromTemplate(template);
}

export function duplicateTemplateName(template: EventTemplate): string {
  const name =
    template.templateName ||
    template.title?.de ||
    template.title?.en ||
    'Event-Vorlage';

  return `${name} Kopie`;
}

export function nextConnectedShiftDate(
  shiftId: string,
  dateShifts: ConnectedTemplateShift[],
  today: string
): string | undefined {
  return dateShifts.reduce<string | undefined>((nextDate, shift) => {
    if (shift.id !== shiftId || !shift.date || shift.date < today) {
      return nextDate;
    }

    return !nextDate || shift.date < nextDate ? shift.date : nextDate;
  }, undefined);
}

function eventForTemplateComparison(event: Event): Event {
  const comparableEvent = eventFromTemplate(event as EventTemplate);

  return {
    ...comparableEvent,
    active: false,
    payAtRestaurant: !!comparableEvent.payAtRestaurant,
  };
}

export type EventTemplateSaveCase =
  | 'standalone'
  | 'detached'
  | 'linked-unchanged'
  | 'linked-changed';

export function getEventTemplateSaveCase(
  event: Event,
  selectedTemplate?: EventTemplate,
  initialTemplateId?: string
): EventTemplateSaveCase {
  if (!selectedTemplate) {
    return initialTemplateId ? 'detached' : 'standalone';
  }

  return isEqual(
    eventForTemplateComparison(event),
    eventForTemplateComparison(eventFromTemplate(selectedTemplate))
  )
    ? 'linked-unchanged'
    : 'linked-changed';
}
